<template>
  <Popup :ref="(el) => popup = el"
         class=""
         :title="$t('labels.payout_upload_files_title')"
         @onClose="hide"
  >
    <div class="popup__content" v-if="deal">
      <div class="download-documents">
        <div class="download-documents__warning">
          <div class="download-documents__icon">
            <svg width="26" height="24" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5759 1.49171C11.6527 -0.375491 14.3471 -0.375491 15.4239 1.49171L25.4719 18.8917C26.5487 20.7589 25.1999 23.0917 23.0463 23.0917H2.95188C0.796679 23.0917 -0.550521 20.7589 0.527879 18.8917L10.5759 1.49171ZM12.9999 5.49971C13.3181 5.49971 13.6234 5.62614 13.8484 5.85118C14.0735 6.07622 14.1999 6.38145 14.1999 6.69971V12.2997C14.1999 12.618 14.0735 12.9232 13.8484 13.1482C13.6234 13.3733 13.3181 13.4997 12.9999 13.4997C12.6816 13.4997 12.3764 13.3733 12.1514 13.1482C11.9263 12.9232 11.7999 12.618 11.7999 12.2997V6.69971C11.7999 6.38145 11.9263 6.07622 12.1514 5.85118C12.3764 5.62614 12.6816 5.49971 12.9999 5.49971ZM12.9999 19.8997C13.4242 19.8997 13.8312 19.7311 14.1313 19.4311C14.4313 19.131 14.5999 18.7241 14.5999 18.2997C14.5999 17.8754 14.4313 17.4684 14.1313 17.1683C13.8312 16.8683 13.4242 16.6997 12.9999 16.6997C12.5755 16.6997 12.1686 16.8683 11.8685 17.1683C11.5685 17.4684 11.3999 17.8754 11.3999 18.2997C11.3999 18.7241 11.5685 19.131 11.8685 19.4311C12.1686 19.7311 12.5755 19.8997 12.9999 19.8997Z" fill="#FF4747"></path>
            </svg>
          </div>
          <div class="download-documents__text">
            <div><b>{{ $t('labels.payout_confirmation_warning')}}</b></div>
          </div>
        </div>
        <div class="download-documents__info">
          <div class="download-documents__main-info">
            <div>
              ID <b>{{ deal?.publicId}}</b>
            </div>
            <div>
              <span class="text-capitalize-first">{{ $t('labels.sum') }}</span>: <b>{{ numberFormatter.formatMoney(deal?.takerSum) }}</b>
            </div>
          </div>
          <div class="download-documents__card-info card-info">
            <div class="card-info__title">
              <PayMethodIcon :method="deal.paymentMethod"></PayMethodIcon>
              <span>{{ $t(`payment_options.${deal.paymentOption}`) }}
                    <span v-if="deal.requisites?.crossBorderCurrency">{{deal.requisites?.crossBorderCurrency}}</span> <span v-if="deal.requisites?.paymentSystem">{{ helper.trans('payment_systems.' + deal.requisites?.paymentSystem) }}</span>
                  </span>
            </div>
            <div class="card-info__number">
              <PayoutQrBlock
                v-if="deal.payoutQr?.link"
                :link="deal.payoutQr.link"
              />
              <VietQrPaymentBlock
                v-else-if="helper.isEmvQrOption(deal.paymentOption)"
                :deal-id="deal.id"
                :qr-code-data="deal.qrCodeData ?? null"
                :qr-code-link="helper.emvQrData(deal)"
                :fallback-requisites="deal.paymentRequisites.requisites || ''"
              />
              <VietQrPaymentBlock
                v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal)"
                :deal-id="deal.id"
                :qr-code-data="null"
                :qr-code-link="helper.dealQrCodeLink(deal)"
                :fallback-requisites="''"
              />
              <small v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR" class="text-secondary">{{ $t('labels.manual_sbp_qr_awaiting_link') }}</small>
              <template v-else>
              <a v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
              <a v-else-if="deal.paymentOption === PaymentOption.CLICK_UZ_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.trans('labels.my_click_uz') }}</a>
              <a v-else-if="deal.paymentOption === PaymentOption.TIPS && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
              <div v-else-if="deal.paymentRequisites.requisites?.startsWith('http')" class="d-flex align-items-center gap-xs">
                <a :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a> <copy-button :value="deal.paymentRequisites.requisites"></copy-button>
              </div>
              <div v-else class="d-flex align-items-center gap-xs">
                {{ deal.paymentRequisites.requisites }} <copy-button :value="deal.paymentRequisites.requisites"></copy-button>
              </div>
              </template>
            </div>
            <div class="card-info__name">
              {{ deal.paymentRequisites.holder }}
            </div>
          </div>
        </div>
      </div>
      <form class="download-form">
        <div class="download-form__container" id="drop-area"  v-bind="dropzone.getRootProps()">
          <input v-bind="dropzone.getInputProps()" />
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M35.25 33H25.5V18.6216L30.4397 23.5603C30.7233 23.8297 31.1009 23.9777 31.4921 23.9727C31.8832 23.9677 32.2569 23.8101 32.5335 23.5335C32.8101 23.2569 32.9677 22.8832 32.9727 22.492C32.9777 22.1009 32.8297 21.7233 32.5603 21.4397L25.0603 13.9397C24.779 13.6586 24.3977 13.5007 24 13.5007C23.6023 13.5007 23.221 13.6586 22.9397 13.9397L15.4397 21.4397C15.1703 21.7233 15.0223 22.1009 15.0273 22.492C15.0323 22.8832 15.1899 23.2569 15.4665 23.5335C15.7431 23.8101 16.1168 23.9677 16.508 23.9727C16.8991 23.9777 17.2767 23.8297 17.5603 23.5603L22.5 18.6216V33H12.75C11.3581 32.9985 10.0236 32.4449 9.03933 31.4607C8.05509 30.4764 7.50149 29.1419 7.5 27.75V8.25C7.50149 6.85807 8.05509 5.52358 9.03933 4.53933C10.0236 3.55509 11.3581 3.00149 12.75 3H35.25C36.6419 3.00149 37.9764 3.55509 38.9607 4.53933C39.9449 5.52358 40.4985 6.85807 40.5 8.25V27.75C40.4985 29.1419 39.9449 30.4764 38.9607 31.4607C37.9764 32.4449 36.6419 32.9985 35.25 33ZM25.5 43.5C25.5 43.8978 25.342 44.2794 25.0607 44.5607C24.7794 44.842 24.3978 45 24 45C23.6022 45 23.2206 44.842 22.9393 44.5607C22.658 44.2794 22.5 43.8978 22.5 43.5V33H25.5V43.5Z" fill="#77818D"></path>
          </svg>
          <div class="download-form__text" id="file-text">
            {{ $t('labels.payout_upload_files')}}
          </div>
        </div>
        <small v-if="stopScamEnabled" class="d-block text-secondary mb-2">{{ $t('stopscam.file_hint') }}</small>
        <div v-for="fileAttachment in fileAttachments" class="row mb-2 ms-2" :key="fileAttachment.id">
          <div class="col-sm-12">
            <svg-icon class="icon icon__warning icon__small" icon="paperclip"/> <span class="me-2">{{ fileAttachment.file.name}}</span>
            <div v-if="fileAttachment.error === null && fileAttachment.attachment === null" class="spinner-border" role="status" style="zoom: 0.5">
              <span class="visually-hidden"></span>
            </div>
            <a v-else href="#" @click.prevent="removeFile(fileAttachment)">
              <svg-icon class="icon icon__attention icon__small" icon="xmark"/>
            </a>
            <div>
              <small class="text-danger" v-if="fileAttachment.error !== null">{{fileAttachment.error}}</small>
            </div>
          </div>
        </div>
        <div class="popup__controls">
          <button type="button" @click="hide" class="btn popup__close">
            <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
          </button>
          <LoaderButton :on-click="confirmPayout" class="btn btn--reset">
            <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.2044 1.6485L4.74285 9.34081C4.67197 9.42524 4.58377 9.49344 4.48423 9.5408C4.3847 9.58817 4.27614 9.61358 4.16592 9.61533H4.15294C4.04512 9.61529 3.93852 9.59259 3.84004 9.54869C3.74156 9.5048 3.65341 9.44069 3.58131 9.36052L0.812078 6.2836C0.741749 6.20901 0.68704 6.12111 0.651168 6.02507C0.615295 5.92903 0.598982 5.82679 0.603186 5.72436C0.607391 5.62193 0.632028 5.52137 0.675651 5.42859C0.719274 5.33581 0.781003 5.2527 0.857208 5.18412C0.933414 5.11554 1.02256 5.06289 1.1194 5.02925C1.21625 4.99562 1.31884 4.98168 1.42115 4.98826C1.52346 4.99484 1.62342 5.02181 1.71515 5.06757C1.80689 5.11334 1.88856 5.17698 1.95535 5.25475L4.13275 7.67399L10.0265 0.659081C10.1587 0.506234 10.3457 0.411551 10.5472 0.395499C10.7486 0.379446 10.9483 0.443313 11.1031 0.573295C11.2578 0.703277 11.3552 0.888932 11.3741 1.09013C11.3931 1.29133 11.3321 1.4919 11.2044 1.6485Z" fill="#F7F9FC"></path>
            </svg>
            <span class="text-capitalize-first">{{ $t('deals.action.confirm_payout') }}</span>
          </LoaderButton>
        </div>
      </form>
    </div>
  </Popup>
</template>
<script lang="ts">
import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import Popup from "@/apps/public/components/Popup.vue";
import axios from "axios";
import {$t, helper} from "@/service/CommonHelper";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import { useStore } from 'vuex';
import { api } from '@/service/APIClient';
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import TraderDealDTO from "@/models/TraderDealDTO";
import {DisputeReason} from "@/models/enum/DisputeReason";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import AttachmentDTO from "@/models/AttachmentDTO";
import {DealStatus} from "@/models/enum/DealStatus";
import SvgIcon from "@/apps/public/components/SvgIcon.vue";
import {useDropzone} from "vue3-dropzone";
import {useNotification} from "@kyvg/vue3-notification";
import {numberFormatter} from "@/service/NumberFormatter";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import VietQrPaymentBlock from "@/components/VietQrPaymentBlock.vue";
import PayoutQrBlock from "@/apps/public/components/PayoutQrBlock.vue";

interface AttachmentFileDTO {
  id: number;
  attachment: AttachmentDTO|null,
  file: File,
  error: string|null
}
let id = 0;
export default defineComponent({
  methods: {$t},
  components: {PayoutQrBlock, VietQrPaymentBlock, CopyButton, PayMethodIcon, SvgIcon, LoaderContent, LoaderButton, FormInputWrapper, Popup},
  setup(props) {
    const notification = useNotification()
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const store = useStore()
    const state = reactive({
      deal: null as TraderDealDTO|null,
      onComplete: null as any,
      fileAttachments: [] as AttachmentFileDTO[],
    });


    const onDrop = async (acceptFiles: any, rejectReasons: any) => {
      for (const file of acceptFiles) {
        state.fileAttachments.push({
          id: id++,
          attachment: null,
          file,
          error: null
        } as AttachmentFileDTO)
      }

      for (const fileAttachment of state.fileAttachments) {
        try {
          fileAttachment.attachment = await api.attachmentsUpload(fileAttachment.file)
        } catch (e) {
          console.log(e);
          if (helper.isValidationErrors(e)) {
            for (const item of helper.getValidationErrors(e)) {
              notification.notify({type: 'error', title: helper.trans(`server_errors.${item.code}`, {}, item.message)});
            }
          } else {
            notification.notify({type: 'error', title: helper.trans(`server_errors.c8c7315c-6186-4719-8b71-5659e16bdcb7`)});
          }
          fileAttachment.error = helper.trans(`server_errors.c8c7315c-6186-4719-8b71-5659e16bdcb7`)
        }

      }
    }
    const dropzone = useDropzone({ onDrop });

    const removeFile = (fileAttachment: AttachmentFileDTO) => {
      state.fileAttachments = state.fileAttachments.filter((file) => file.id !== fileAttachment.id)
    }


    const getForm = () => {
      return {
        attachments: state.fileAttachments.filter((file) => file.attachment !== null).map((file) => file.attachment!.id)
      }
    }

    const confirmPayout = async () => {
      if (!state.deal) {
        return
      }

      try {
        await api.dealChangeStatus(state.deal.id, 'confirm', getForm());
        hide()
        if (state.onComplete) {
          state.onComplete()
        }
      } catch (e) {
        if (helper.isValidationErrors(e)) {
          for (const item of helper.getValidationErrors(e)) {
            notification.notify({type: 'error', title: helper.trans(`server_errors.${item.code}`, {}, item.message)});
          }
        }
        console.log(e)
        throw e
      }
    }

    const show = (deal: TraderDealDTO, onComplete: any) => {
      popup.value?.show()
      state.deal = deal

      state.fileAttachments = []
      state.onComplete = onComplete
    }

    const hide = () => {
      popup.value?.hide()
    }

    return {
      ...toRefs(state),
      stopScamEnabled: computed(() => !!store.getters.getAppData?.af_stopscam_payout_receipt_enabled),
      dropzone,
      removeFile,
      confirmPayout,
      DisputeReason,
      DealStatus,
      popup,
      helper,
      show,
      hide,
      PaymentOption,
      numberFormatter,
    }
  }
})
</script>
